import { Box, Typography, useTheme } from '@mui/material';
import { WaiverRequests } from '@prisma/client';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmptyOutlined';
import { LightTooltip } from '../../global/LightTooltip';
import {
  GetWaiversData,
  WaiverRequestWithCustomerAndEvents,
} from '../../../types/waivers';
import { getWaiverRequestStatus } from '../../../helpers/waivers';
import { QueueAppointment } from '../../../types/queue';

interface Props {
  appointment:
    | WaiverRequestWithCustomerAndEvents['appointment']
    | QueueAppointment
    | null
    | undefined;
  waiverRequest: WaiverRequests | null | undefined;
  waivers: GetWaiversData[] | null | undefined;
}

export default function WaiverStatusBadge({
  appointment,
  waiverRequest,
  waivers,
}: Props) {
  const theme = useTheme();

  let badgeText = '';
  let badgeIcon = null;
  let backgroundColor = '';
  let textColor = '';
  let tooltipText = '';
  let borderColor = '';

  const status = getWaiverRequestStatus({
    appointment,
    waiverRequest,
    waivers,
  });

  if (waiverRequest?.status === 'QUEUED') {
    badgeText = 'Queued';
    backgroundColor = theme.palette.lightGrey.main;
    textColor = 'text.primary';
    borderColor = theme.palette.lightOutlineGrey.main;
    tooltipText =
      'Waiver link will be sent to customer on the day of their appointment';
    badgeIcon = (
      <AccessTimeIcon
        sx={{
          color: 'text.primary',
          width: '16px',
          height: '16px',
        }}
      />
    );
  } else if (status === 'canceled') {
    badgeText = 'Canceled';
    backgroundColor = theme.palette.primary.light;
    textColor = theme.palette.primary.main;
    borderColor = '#FFC2B6';
    tooltipText = 'Waiver request was canceled';
    badgeIcon = (
      <CloseIcon
        sx={{
          color: theme.palette.primary.main,
          width: '16px',
          height: '16px',
        }}
      />
    );
  } else if (status === 'notCompleted') {
    badgeText = 'Sent';
    backgroundColor = '#fdf8c9';
    textColor = '#b13600';
    borderColor = '#fbd992';
    tooltipText = 'Waiver link has been sent to the customer';
    badgeIcon = (
      <AccessTimeIcon
        sx={{
          color: 'warning.dark',
          width: '16px',
          height: '16px',
        }}
      />
    );
  } else if (status === 'complete') {
    badgeText = 'Signed';
    backgroundColor = theme.palette.accent.main;
    textColor = theme.palette.secondary.main;
    borderColor = '#AAF2DA';
    tooltipText = 'Waiver has been signed by the customer';
    badgeIcon = (
      <CheckIcon
        sx={{
          color: theme.palette.secondary.main,
          width: '16px',
          height: '16px',
        }}
      />
    );
  } else if (status === 'pendingEmployee') {
    badgeText = 'Pending employee';
    backgroundColor = '#fdf8c9';
    textColor = '#b13600';
    borderColor = '#fbd992';
    tooltipText = 'Employee must complete their portion of the waiver';
    badgeIcon = (
      <HourglassEmptyIcon
        sx={{
          color: 'warning.dark',
          width: '16px',
          height: '16px',
        }}
      />
    );
  } else {
    badgeText = 'No waiver';
    backgroundColor = theme.palette.lightGrey.main;
    textColor = 'text.primary';
    borderColor = theme.palette.lightOutlineGrey.main;
    tooltipText = 'No waiver request';
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <LightTooltip
        title={tooltipText}
        placement="bottom"
        arrow
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -10],
                },
              },
            ],
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '2px 6px',
            height: '22px',
            boxSizing: 'border-box',
            backgroundColor,
            borderRadius: '4px',
            gap: '4px',
            borderColor,
            borderWidth: '1px',
            borderStyle: 'solid',
            flexShrink: 1,
          }}>
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: textColor,
              lineHeight: '12px',
            }}
            fontWeight={600}>
            {badgeText}
          </Typography>
          {badgeIcon}
        </Box>
      </LightTooltip>
    </Box>
  );
}
